import SidePanel from '../SidePanel.jsx';
import { useContext, useEffect, useState } from 'react';
import TextInput from '../TextInput';
import useBusinessPostApi from '../../lib/useBusinessPostApi.js';
import * as Toast from '../../lib/Toast.js';
import { formatDateTime } from '../../lib/DateTime.js';
import Question from '../forms/Question';
import ClientPicker from '../ClientPicker.jsx';
import { ErrorContext } from '../../lib/ErrorContext';
import AdminSchedulingQuestion from './AdminSchedulingQuestion.jsx';
import OptionalTimeZoneSelect from './OptionalTimeZoneSelect.jsx';
import EarthIcon from '../icons/EarthIcon.jsx';
import { useEventTypesQuery } from '../../lib/SharedQueries.js';
import { useBusinessInfo, useFormsQuery } from '~/lib/Queries';
import Select from '~/components/Select';
import AdminAddonSelection from '~/components/scheduling/AdminAddonSelection';
import OptionToggle from '~/components/OptionToggle.jsx';
import EditFormAdminScheduling from '~/components/scheduling/EditFormAdminScheduling.jsx';
import AdminSelectForm from '~/components/scheduling/AdminSelectForm.jsx';
import AdminSelectAndEditForm from '~/components/scheduling/AdminSelectAndEditForm.jsx';

const SchedulingPanel = ({
    isOpen,
    onClose,
    start,
    client,
    resourceId,
    eventTypeId,
    onScheduled,
}) => {
    const { errors } = useContext(ErrorContext);

    const [formData, setFormData] = useState({
        scheduling: [],
        client: client || {},
        formId: null,
    });
    const [initialStart, setInitialStart] = useState(false);
    const [isUnavailable, setIsUnavailable] = useState(false);
    const [dontTriggerFlows, setDontTriggerFlows] = useState(false);

    const hasPickedEvent =
        !!formData.scheduling[0]?.eventType &&
        !!formData.scheduling[0]?.bookableResource;

    const { save, isSaving } = useBusinessPostApi(`/schedule`);
    const { data: allEventTypesData } = useEventTypesQuery();
    const { data: businessData } = useBusinessInfo();
    const isFixedTimezone = !!businessData?.business?.fixed_timezone;

    useEffect(() => {
        if (isOpen) {
            // Reset...
            const schedulingData = {};
            if (start) schedulingData.startTime = start;
            if (resourceId)
                schedulingData.bookableResource = parseInt(resourceId);
            if (eventTypeId)
                schedulingData.eventType = eventTypeId;
            setDontTriggerFlows(false);

            setFormData(old => ({
                ...old,
                scheduling: [{ ...schedulingData }],
                client: client || {},
                timezone: '',
                answers: {},
            }));

            setInitialStart(start);
        }
    }, [isOpen, start]);

    useEffect(() => {
        if (isOpen) {
            // They clicked another time while this was open!
            setFormData(old => ({
                ...old,
                scheduling: [
                    {
                        //...formData.scheduling[0],
                        startTime: start,
                    },
                ],
            }));
        }
    }, [start?.toISOString()]);

    function doSave() {
        if (isUnavailable) {
            if (
                !confirm(
                    'This time is outside business hours or conflicts. Are you sure you want to schedule it?',
                )
            ) {
                return;
            }
        }
        save({ ...formData, dontTriggerFlows }).then(result => {
            onClose();
            if (onScheduled) {
                onScheduled(result);
            }

            if (result.length > 1) {
                Toast.success(
                    `Successfully scheduled ${result.length} appointments`,
                );
            } else {
                Toast.success(
                    `Successfully scheduled appointment on ${formatDateTime(result[0].client_start)}`,
                );
            }
        });
    }

    const questionSettings = {};
    if (start && allEventTypesData) {
        questionSettings['eventTypes'] = allEventTypesData
            .filter(t => !t.is_class)
            .map(t => t.id);
    }

    return (
        <SidePanel
            isOpen={isOpen}
            onClose={onClose}
            title="New Appointment"
            showSave={false}
        >
            <div className="relative z-10">
                <AdminSchedulingQuestion
                    key={initialStart}
                    value={formData.scheduling}
                    setValue={value => {
                        setFormData(old => {
                            if (typeof value === 'function') {
                                value = value(old?.scheduling);
                            }

                            return {
                                ...old,
                                scheduling: value, //...old.scheduling
                            };
                        });
                    }}
                    isUnavailable={isUnavailable}
                    setIsUnavailable={setIsUnavailable}
                    settings={{
                        questionSettings: questionSettings,
                    }}
                />
            </div>

            {hasPickedEvent && (
                <div>
                    {!isFixedTimezone && (
                        <div className="mt-2 flex justify-end">
                            <div>
                                <OptionalTimeZoneSelect
                                    value={formData?.clientTimezone}
                                    onChange={value =>
                                        setFormData({
                                            ...formData,
                                            clientTimezone: value,
                                        })
                                    }
                                    defaultLabel={
                                        <div className="flex items-center">
                                            <EarthIcon className="w-4 h-4 mr-1.5" />
                                            Choose client time zone
                                        </div>
                                    }
                                    inputLabel="Client Time Zone"
                                    btnClassName="text-sm !no-underline mt-0"
                                />
                                {!!formData?.clientTimezone && (
                                    <div className="text-gray-500 text-sm ml-4 mb-2">
                                        Times shown above are in your business's
                                        time zone. Notifications sent to the
                                        client will be in their time zone.
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <AdminSelectAndEditForm
                        formId={formData?.formId}
                        setFormId={value => {
                            setFormData(old => ({
                                ...old,
                                formId: value,
                            }));
                        }}
                        answers={formData?.answers}
                        setAnswers={newAnswers =>
                            setFormData(old => ({
                                ...old,
                                answers: newAnswers(old?.answers),
                            }))
                        }
                        eventTypeId={formData.scheduling[0]?.eventType}
                    />

                    <AdminAddonSelection
                        eventTypeId={formData.scheduling[0]?.eventType}
                        formId={formData?.formId}
                        className="mt-4"
                        value={formData?.answers}
                        onChange={value =>
                            setFormData(old => ({
                                ...old,
                                answers: value,
                            }))
                        }
                    />
                </div>
            )}

            <div className="panel-label mt-4">Client</div>
            <ClientPicker
                value={formData.client}
                setValue={value =>
                    setFormData(old => ({ ...old, client: value }))
                }
                initialSearch={client?.email}
                setTimezone={value =>
                    setFormData(old => ({ ...old, clientTimezone: value }))
                }
            />

            {errors && errors['client.email'] && (
                <div className="text-red-600">
                    A client is required, please select an existing client or
                    add a new client.
                </div>
            )}

            <OptionToggle
                value={!dontTriggerFlows}
                onChange={value => setDontTriggerFlows(!value)}
                className="text-gray-500 text-sm flex-row-reverse mt-4 gap-x-4"
                title="Send automations when scheduled"
            />

            <div className="flex items-center space-x-6 mt-6">
                <button
                    type="button"
                    className="btn-save flex-1"
                    onClick={doSave}
                    disabled={isSaving}
                >
                    {isSaving ? 'Scheduling...' : 'Schedule'}
                </button>
                <button
                    type="button"
                    className="btn-gray-pill-lg flex-1"
                    onClick={onClose}
                >
                    Cancel
                </button>
            </div>
        </SidePanel>
    );
};

export default SchedulingPanel;
